<script setup>
import { computed, ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { API, HOST } from '@/utils/http'
import { firebase } from '@/router'
import { Zap, Check, Settings } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DialogPlanSelect } from '@/components/dialogs'
import { useCheckOrgAccess } from '@/composables/useCheckAccess'
import { useFeatures } from '@/composables/useFeatures'

const store = useStore()
const route = useRoute()
const loading = ref(false)
const plans = ref({})
const dialogPlanSelectRef = ref(null)

const { showBillingPage, showUpgradeButtons, showCustomerPortal } = useFeatures()

const orgId = computed(() => parseInt(route.params.org_id))
const org = computed(() => store.getters['org/getCurrent'])
const hasBillingAccess = computed(() => useCheckOrgAccess(org.value, 'org_billing', ['read']))

const currentPlan = computed(() => {
  if (!org.value) return 'free'

  const tierMapping = {
    0: 'free',
    1: 'basic',
    2: 'unlimited'
  }

  const planValue = org.value.plan !== undefined ? org.value.plan : 0
  return tierMapping[planValue] || 'free'
})

const currentPlanData = computed(() => {
  if (!plans.value || Object.keys(plans.value).length === 0) {
    return null
  }

  return plans.value[currentPlan.value]
})

const formatLimit = (limit) => {
  return limit === null ? 'Unlimited' : limit.toString()
}

const fetchPlans = async () => {
  try {
    loading.value = true

    const response = await API({
      method: 'GET',
      url: `${HOST}/organization/${org.value.organization_id}/plans`
    })

    plans.value = response.data
  } catch (error) {
    console.error('Error fetching plans:', error)
  } finally {
    loading.value = false
  }
}

const openPlanDialog = () => {
  if (dialogPlanSelectRef.value) {
    dialogPlanSelectRef.value.open()
  }
}

const handleManageSubscription = async () => {
  try {
    loading.value = true
    const response = await API({
      method: 'GET',
      url: `${HOST}/organization/${org.value.organization_id}/customer-portal`
    })

    // Redirect to the Stripe customer portal
    if (response.data && response.data.portal_url) {
      window.location.href = response.data.portal_url
    } else {
      console.error('No portal URL received')
    }
  } catch (error) {
    console.error('Error opening customer portal:', error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  if (org.value && hasBillingAccess.value && showBillingPage.value) {
    fetchPlans()
  }
})
</script>

<template>
  <div class="space-y-6">
    <!-- Feature Disabled Message -->
    <div v-if="!showBillingPage" class="border rounded-lg p-6 text-center max-w-md">
      <p class="text-muted-foreground">Billing features are currently unavailable.</p>
    </div>
    
    <!-- Access Denied Message -->
    <div v-else-if="org && !hasBillingAccess" class="border rounded-lg p-6 text-center max-w-md">
      <p class="text-muted-foreground">You don't have permission to access billing settings. Only organization administrators can manage billing.</p>
    </div>

    <!-- Current Plan Card -->
    <div v-else-if="currentPlanData" class="max-w-md">
      <div class="border rounded-lg p-6 space-y-4">
        <!-- Header -->
        <div class="flex items-center justify-between">
          <Badge :variant="currentPlan === 'unlimited' ? 'default' : 'secondary'" class="text-sm capitalize">
            {{ currentPlanData.name }}
          </Badge>
        </div>

        <p class="text-sm text-muted-foreground">{{ currentPlanData.description }}</p>

        <!-- Price -->
        <div class="flex items-baseline space-x-1">
          <span class="text-3xl font-bold">${{ currentPlanData.price_monthly }}</span>
          <span class="text-muted-foreground">/month</span>
        </div>

        <!-- Features -->
        <div class="space-y-2">
          <p class="text-sm font-medium">Features included:</p>
          <ul class="space-y-1">
            <li class="flex items-center text-sm text-muted-foreground">
              <Check class="h-4 w-4 mr-2 text-green-500" />
              Max Projects: {{ formatLimit(currentPlanData.max_projects) }}
            </li>
            <li class="flex items-center text-sm text-muted-foreground">
              <Check class="h-4 w-4 mr-2 text-green-500" />
              Team Members: {{ formatLimit(currentPlanData.max_team_members) }}
            </li>
          </ul>
        </div>

        <!-- Actions -->
        <div class="flex justify-between pt-4 border-t">
          <DialogPlanSelect ref="dialogPlanSelectRef" v-if="showUpgradeButtons">
            <Button
              v-if="currentPlan === 'free'"
              @click="openPlanDialog"
              variant="default"
              class="flex items-center gap-2"
            >
              <Zap class="h-4 w-4" />
              Add Plan
            </Button>
            <Button
              v-else-if="currentPlan === 'basic'"
              @click="openPlanDialog"
              variant="default"
              class="flex items-center gap-2"
            >
              <Zap class="h-4 w-4" />
              Upgrade
            </Button>
            <div v-else class="flex items-center text-sm text-muted-foreground">
              <Check class="h-4 w-4 mr-2 text-green-500" />
              You have the highest tier plan
            </div>
          </DialogPlanSelect>
          <div v-else-if="currentPlan !== 'free'" class="flex items-center text-sm text-muted-foreground">
            <Check class="h-4 w-4 mr-2 text-green-500" />
            {{ currentPlanData.name }} Plan Active
          </div>

          <Button
            v-if="currentPlan !== 'free' && showCustomerPortal"
            @click="handleManageSubscription"
            variant="outline"
            class="flex items-center gap-2"
            :disabled="loading"
          >
            <Settings class="h-4 w-4" />
            Manage subscription
          </Button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else-if="loading" class="flex justify-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>

    <!-- Error State -->
    <div v-else class="border rounded-lg p-6 text-center max-w-md">
      <p class="text-muted-foreground">Unable to load billing information. Please try again later.</p>
    </div>
  </div>
</template>
